import React, { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { formatRoute } from 'react-router-named-routes';
import DeleteIcon from '@mui/icons-material/Delete';
import Icon from '@mdi/react';
import { mdiFileMove } from '@mdi/js';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { useDispatch, useSelector } from 'react-redux';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import { MovableCardWrapper } from '../common/board/styles/Base';
import contentEntryPlaceholder from '../../assets/images/content_entry_placeholder.png';
import SharedContentLinkToProgrammeModal from '../programmes/shared_content_link_to_programme_modal';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useGetOrCreateSpaceContentBoardsQuery,
  useGetSharedContentEntryByIDAndParentQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { spaceContentBoardEntryQuickDuplicate } from '../../operations/spaces_operations';
import {
  useCreateSharedContentEntryMutation,
  useGetSpaceQuery,
  useLazyGetProgrammeQuery,
  useLazyGetSharedContentEntryQuery,
  useUpdateProgrammeMutation,
  useUpdateSharedContentBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { ActivityObjectType, EntryType } from '../../API';
import {
  programmePlanWithEntryAdded,
  programmeWithPlanUpdated,
} from '../../services/programmes';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const SpaceContentBoardContentEntryCard = ({
  id,
  appBoardId,
  title,
  style,
  className,
  onClick,
  onDelete,
}) => {
  const dispatch = useDispatch();

  const { data: healthProSpace } = useGetSpaceQuery();

  const setGlobalSnackbar = useCallback(
    (obj) => dispatch(showGlobalSnackbar(obj)),
    [dispatch],
  );

  const [linkToProgrammeAnchorEl, setLinkToProgrammeAnchorEl] = useState(null);

  const sharedContentEntryID = style._objectID;

  const { data: sharedContentBoards } = useGetOrCreateSpaceContentBoardsQuery();

  const sharedContentBoard = (sharedContentBoards || []).find(
    (b) => b.id === appBoardId,
  );

  const { data: sharedContentEntry } =
    useGetSharedContentEntryByIDAndParentQuery(
      { id: sharedContentEntryID, parentID: appBoardId },
      { skip: !sharedContentEntryID || !appBoardId },
    );

  const [lazyGetSharedContentEntry] = useLazyGetSharedContentEntryQuery();

  const [createSharedContentEntry] = useCreateSharedContentEntryMutation();

  const [updateSharedContentBoard] = useUpdateSharedContentBoardMutation();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const [deleteContentEntryAnchorEl, setDeleteContentEntryAnchorEl] =
    useState(null);

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const onQuickDuplicate = async () => {
    spaceContentBoardEntryQuickDuplicate(
      sharedContentBoard,
      id,
      lazyGetSharedContentEntry,
      createSharedContentEntry,
      updateSharedContentBoard,
    );
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
      lastActivityObjectID: sharedContentBoard.id,
    });
  };

  const coverImageUrl = sharedContentEntry?.coverImageUrl;

  const onClickLinkToProgramme = (ev) => {
    setLinkToProgrammeAnchorEl(ev.target);
    ev.stopPropagation();
  };

  const [lazyGetProgramme] = useLazyGetProgrammeQuery();

  const onLinkToProgramme = async (toProgrammeID, toPlanID, toDayID) => {
    const { data: programme } = await lazyGetProgramme(toProgrammeID);
    const plan = programme.plans.find((pl) => pl.id === toPlanID);
    const entryID = uuidv4();

    const updatedPlan = programmePlanWithEntryAdded(
      plan,
      [toDayID],
      entryID,
      EntryType.SHARED_CONTENT_ENTRY_REFERENCE,
      sharedContentEntryID,
    );

    const updatedProgramme = programmeWithPlanUpdated(updatedPlan);
    return updateProgramme(updatedProgramme);
  };

  if (!sharedContentEntry) {
    return null;
  }

  return (
    <MovableCardWrapper
      data-id={id}
      onClick={() => onClick(id)}
      style={style}
      className={className}>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <Grid item xs={12}>
          <CardMedia
            component="img"
            className="contentEntryCardImage"
            image={coverImageUrl || contentEntryPlaceholder}
            alt=""
          />
        </Grid>
        <Grid item xs={12}>
          <CardContent
            sx={{
              flex: '1 0 auto',
              margin: '8px',
              padding: '0 !important',
              fontSize: '14px',
              whiteSpace: 'break-spaces',
            }}
            style={{
              maxWidth: 156,
              lineClamp: 3,
              WebkitLineClamp: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
            }}>
            {title}
          </CardContent>
        </Grid>
        <Grid item xs={12} sx={{ margin: '2px 0 4px' }}>
          <Grid container>
            <Grid item xs={8}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '4px',
                }}>
                <Tooltip title="Copy to program" placement="top-start">
                  <IconButton
                    aria-label="Copy to program"
                    size="small"
                    onClick={onClickLinkToProgramme}>
                    <Icon
                      path={mdiFileMove}
                      style={{ width: '24px', height: '24px' }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Duplicate this content entry"
                  placement="top-start">
                  <IconButton
                    aria-label="quick duplicate"
                    size="small"
                    onClick={(ev) => {
                      onQuickDuplicate();
                      ev.stopPropagation();
                    }}>
                    <FileCopyIcon />
                  </IconButton>
                </Tooltip>
                <IconButton
                  aria-label="delete"
                  size="small"
                  onClick={(ev) => {
                    setDeleteContentEntryAnchorEl(ev.target);
                    ev.stopPropagation();
                  }}>
                  <DeleteIcon />
                </IconButton>
                <DeleteCardConfirmPopover
                  visible={!!deleteContentEntryAnchorEl}
                  anchorEl={deleteContentEntryAnchorEl}
                  title="Delete content entry"
                  message="The content entry will be deleted permanently. Are you sure?"
                  height={155}
                  onConfirmDelete={() => onDelete(id)}
                  onDismiss={() => setDeleteContentEntryAnchorEl(null)}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Card>
      {linkToProgrammeAnchorEl && (
        <SharedContentLinkToProgrammeModal
          anchorEl={linkToProgrammeAnchorEl}
          onLink={(toProgrammeID, toPlanID, toDayID) => {
            onLinkToProgramme(toProgrammeID, toPlanID, toDayID);
            setLinkToProgrammeAnchorEl(null);
            setGlobalSnackbar({
              notificationText: 'Your meal has been copied to your program',
              linkText: 'View program',
              linkTarget: formatRoute(
                '/programmes/:programmeId/plans/:planId',
                {
                  programmeId: toProgrammeID,
                  planId: toPlanID,
                },
              ),
            });
          }}
          onDismiss={() => setLinkToProgrammeAnchorEl(null)}
        />
      )}
    </MovableCardWrapper>
  );
};

SpaceContentBoardContentEntryCard.propTypes = {
  id: PropTypes.string.isRequired,
  appBoardId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default SpaceContentBoardContentEntryCard;
